// frontend/src/pages/AdminLanguages.jsx
import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  LanguageIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const AdminLanguages = () => {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('language_name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    language_code: '',
    language_name: '',
    is_active: true
  });

  // Load languages
  const loadLanguages = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getLanguages();
      if (!response.data.error) {
        let filteredLanguages = response.data.data.languages;

        // Apply search filter
        if (searchTerm) {
          filteredLanguages = filteredLanguages.filter(lang =>
            lang.language_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lang.language_code.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }

        // Apply status filter
        if (statusFilter !== '') {
          const isActive = statusFilter === 'active';
          filteredLanguages = filteredLanguages.filter(lang => Boolean(lang.is_active) === isActive);
        }

        // Apply sorting
        filteredLanguages.sort((a, b) => {
          let aValue = a[sortBy];
          let bValue = b[sortBy];

          // Handle special cases
          if (sortBy === 'usage_count') {
            aValue = parseInt(aValue) || 0;
            bValue = parseInt(bValue) || 0;
          } else if (sortBy === 'created_at') {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
          } else {
            aValue = String(aValue).toLowerCase();
            bValue = String(bValue).toLowerCase();
          }

          if (sortOrder === 'asc') {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          } else {
            return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
          }
        });

        setLanguages(filteredLanguages);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to load languages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLanguages();
  }, [searchTerm, statusFilter, sortBy, sortOrder]);

  // Handle form changes
  const handleFormChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  // Create language
  const handleCreateLanguage = async (e) => {
    e.preventDefault();
    try {
      const response = await adminAPI.createLanguage(formData);
      if (!response.data.error) {
        setSuccess('Language created successfully');
        setShowCreateModal(false);
        resetForm();
        loadLanguages();
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to create language');
    }
  };

  // Update language
  const handleUpdateLanguage = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        language_code: formData.language_code,
        language_name: formData.language_name,
        is_active: formData.is_active
      };
      const response = await adminAPI.updateLanguage(selectedLanguage.id, updateData);
      if (!response.data.error) {
        setSuccess('Language updated successfully');
        setShowEditModal(false);
        resetForm();
        loadLanguages();
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to update language');
    }
  };

  // Delete language
  const handleDeleteLanguage = async () => {
    try {
      const response = await adminAPI.deleteLanguage(selectedLanguage.id);
      if (!response.data.error) {
        setSuccess('Language deleted successfully');
        setShowDeleteModal(false);
        loadLanguages();
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to delete language');
    }
  };

  // Toggle language status
  const handleToggleLanguageStatus = async () => {
    try {
      const newStatus = !selectedLanguage.is_active;
      const response = await adminAPI.updateLanguage(selectedLanguage.id, { is_active: newStatus });
      if (!response.data.error) {
        setSuccess(`Language ${newStatus ? 'enabled' : 'disabled'} successfully`);
        setShowToggleModal(false);
        loadLanguages();
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to update language status');
    }
  };

  // Modal handlers
  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (language) => {
    setSelectedLanguage(language);
    setFormData({
      language_code: language.language_code,
      language_name: language.language_name,
      is_active: Boolean(language.is_active)
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (language) => {
    setSelectedLanguage(language);
    setShowDeleteModal(true);
  };

  const openToggleModal = (language) => {
    setSelectedLanguage(language);
    setShowToggleModal(true);
  };

  const resetForm = () => {
    setFormData({
      language_code: '',
      language_name: '',
      is_active: true
    });
    setSelectedLanguage(null);
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowToggleModal(false);
    resetForm();
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Clear messages
  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  if (loading && languages.length === 0) {
    return <LoadingSpinner message="Loading languages..." />;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex justify-center items-center w-10 h-10 bg-indigo-100 rounded-lg">
            <GlobeAltIcon className="w-6 h-6 text-indigo-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Language Management</h1>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md border border-transparent shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="mr-2 w-4 h-4" />
          Add New Language
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="relative px-4 py-3 mb-6 text-red-700 bg-red-50 rounded border border-red-200">
          {error}
          <button onClick={clearMessages} className="absolute top-0 right-0 bottom-0 px-4 py-3">
            <span className="sr-only">Dismiss</span>&times;
          </button>
        </div>
      )}

      {success && (
        <div className="relative px-4 py-3 mb-6 text-green-700 bg-green-50 rounded border border-green-200">
          {success}
          <button onClick={clearMessages} className="absolute top-0 right-0 bottom-0 px-4 py-3">
            <span className="sr-only">Dismiss</span>&times;
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 card">
        <div className="pt-6 card-content">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search languages by name or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="py-2 pr-4 pl-10 w-full rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="sm:w-40">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Sort By */}
            <div className="sm:w-40">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="language_name">Language Name</option>
                <option value="language_code">Language Code</option>
                <option value="usage_count">Usage Count</option>
                <option value="created_at">Created Date</option>
              </select>
            </div>

            {/* Sort Order */}
            <div className="sm:w-36">
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="px-3 py-2 pr-8 w-full rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Languages Table */}
      <div className="mb-6 card">
        <div className="pt-6 card-content">
          {loading ? (
            <div className="py-8 text-center">
              <LoadingSpinner />
            </div>
          ) : languages.length === 0 ? (
            <div className="py-12 text-center">
              <div className="flex justify-center items-center mx-auto mb-4 w-12 h-12 bg-gray-100 rounded-lg">
                <LanguageIcon className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-medium text-gray-900">No languages found</h3>
              <p className="text-gray-500">Try adjusting your search criteria or add a new language</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 font-medium text-left text-gray-900">Language</th>
                    <th className="px-4 py-3 font-medium text-left text-gray-900">Code</th>
                    <th className="px-4 py-3 font-medium text-left text-gray-900">Status</th>
                    <th className="px-4 py-3 font-medium text-left text-gray-900">Usage Count</th>
                    <th className="px-4 py-3 font-medium text-left text-gray-900">Created Date</th>
                    <th className="px-4 py-3 font-medium text-right text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {languages.map((language) => (
                    <tr key={language.id} className={`border-b border-gray-100 hover:bg-gray-50 ${!language.is_active ? 'bg-gray-50 opacity-75' : ''}`}>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-10 h-10">
                            <div className="flex justify-center items-center w-10 h-10 bg-indigo-100 rounded-full">
                              <GlobeAltIcon className="w-5 h-5 text-indigo-500" />
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="font-medium text-gray-900">
                              {language.language_name}
                              {!language.is_active && (
                                <span className="inline-flex px-2 py-1 ml-2 text-xs font-medium text-gray-800 bg-gray-100 rounded-full">
                                  Disabled
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
                          {language.language_code}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          {language.is_active ? (
                            <span className="inline-flex px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full">
                              Inactive
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {language.usage_count || 0} batches
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {formatDate(language.created_at)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end items-center space-x-2">
                          <button
                            onClick={() => openEditModal(language)}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-md border border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            title="Edit Language"
                          >
                            <PencilIcon className="mr-1 w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => openToggleModal(language)}
                            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                              language.is_active
                                ? 'text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 hover:border-red-300 focus:ring-red-500'
                                : 'text-green-700 bg-green-50 border border-green-200 hover:bg-green-100 hover:border-green-300 focus:ring-green-500'
                            }`}
                            title={language.is_active ? "Disable Language" : "Enable Language"}
                          >
                            {language.is_active ? (
                              <XCircleIcon className="mr-1 w-4 h-4" />
                            ) : (
                              <CheckCircleIcon className="mr-1 w-4 h-4" />
                            )}
                            {language.is_active ? 'Disable' : 'Enable'}
                          </button>
                          {(language.usage_count || 0) === 0 && (
                            <button
                              onClick={() => openDeleteModal(language)}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-md border border-red-200 hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                              title="Delete Language"
                            >
                              <TrashIcon className="mr-1 w-4 h-4" />
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Language Modal */}
      {showCreateModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-lg">
            <h2 className="mb-4 text-xl font-bold">Add New Language</h2>
            <form onSubmit={handleCreateLanguage}>
              <div>
                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Language Code *
                  </label>
                  <input
                    type="text"
                    name="language_code"
                    value={formData.language_code}
                    onChange={handleFormChange}
                    required
                    pattern="[a-z]{2,3}(-[a-z]{2,4})?"
                    title="Language code (e.g., en, de, fr, en-us, en-gb, zh-cn)"
                    placeholder="e.g., en, de, fr, en-us, en-gb"
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Language code with optional region (e.g., en, en-us, zh-cn)</p>
                </div>

                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Language Name *
                  </label>
                  <input
                    type="text"
                    name="language_name"
                    value={formData.language_name}
                    onChange={handleFormChange}
                    required
                    maxLength="50"
                    placeholder="e.g., English, German, French"
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleFormChange}
                      className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">Active languages are available for batch creation</p>
                </div>
              </div>

              <div className="flex justify-end pt-4 space-x-2">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md border border-transparent shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <GlobeAltIcon className="mr-2 w-4 h-4" />
                  Add Language
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Language Modal */}
      {showEditModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-lg">
            <h2 className="mb-4 text-xl font-bold">Edit Language</h2>
            <form onSubmit={handleUpdateLanguage}>
              <div>
                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Language Code *
                  </label>
                  <input
                    type="text"
                    name="language_code"
                    value={formData.language_code}
                    onChange={handleFormChange}
                    required
                    pattern="[a-z]{2,3}(-[a-z]{2,4})?"
                    title="Language code (e.g., en, de, fr, en-us, en-gb, zh-cn)"
                    placeholder="e.g., en, de, fr, en-us, en-gb"
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Language code with optional region (e.g., en, en-us, zh-cn)</p>
                </div>

                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Language Name *
                  </label>
                  <input
                    type="text"
                    name="language_name"
                    value={formData.language_name}
                    onChange={handleFormChange}
                    required
                    maxLength="50"
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleFormChange}
                      className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">Active languages are available for batch creation</p>
                </div>
              </div>

              <div className="flex justify-end pt-4 space-x-2">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md border border-transparent shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <PencilIcon className="mr-2 w-4 h-4" />
                  Update Language
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Language Modal */}
      {showDeleteModal && selectedLanguage && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-lg">
            <h2 className="mb-4 text-xl font-bold">Delete Language</h2>
            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to delete "{selectedLanguage.language_name}"? This action cannot be undone.
              </p>
              <div className="p-3 mt-4 text-red-700 bg-red-50 rounded border border-red-200">
                <p className="text-sm">
                  <strong>Warning:</strong> This language can only be deleted because it has not been used in any batches yet.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={closeModals}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteLanguage}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md border border-transparent shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <TrashIcon className="mr-2 w-4 h-4" />
                Delete Language
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Language Status Modal */}
      {showToggleModal && selectedLanguage && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-lg">
            <h2 className="mb-4 text-xl font-bold">
              {selectedLanguage.is_active ? 'Disable Language' : 'Enable Language'}
            </h2>
            <div className="mb-6">
              <p className="text-gray-600">
                {selectedLanguage.is_active
                  ? `Are you sure you want to disable "${selectedLanguage.language_name}"? It will no longer be available for new batch creation.`
                  : `Are you sure you want to enable "${selectedLanguage.language_name}"? It will become available for new batch creation.`
                }
              </p>
              {selectedLanguage.usage_count > 0 && (
                <div className="p-3 mt-4 text-blue-700 bg-blue-50 rounded border border-blue-200">
                  <p className="text-sm">
                    This language is currently used in {selectedLanguage.usage_count} batch(es). Existing batches will not be affected.
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={closeModals}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button 
                onClick={handleToggleLanguageStatus}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  selectedLanguage.is_active
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                    : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                }`}
              >
                {selectedLanguage.is_active ? (
                  <XCircleIcon className="mr-2 w-4 h-4" />
                ) : (
                  <CheckCircleIcon className="mr-2 w-4 h-4" />
                )}
                {selectedLanguage.is_active ? 'Disable Language' : 'Enable Language'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLanguages;